import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Menu, X } from 'lucide-react';
import { Logo } from './Logo';
import { cn } from '../../utils/cn';

export const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleNavigation = (href: string) => {
    navigate(href);
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false); // Close mobile menu if open
  };

  const navItems = [
    { label: 'Home', href: '/' },
    { label: 'Products', href: '/products' },
    { label: 'News', href: '/news' },
    { label: 'About Us', href: '/about' },
    { label: 'Contact Us', href: '/contact' },
  ];

  const isActiveLink = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    // For other routes, check if the current path starts with the href
    // This ensures /news/article-slug highlights the News nav item
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#F5E6D3] border-b border-[#E8D5C4] transition-colors duration-300">
      <div className="h-16 px-4 md:px-16 flex items-center justify-between">
        <Logo />

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-2">
          <div className="flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavigation(item.href)}
                className={cn(
                  'relative px-4 py-2.5 rounded-lg font-cairo text-sm font-medium transition-all duration-200 ease-in-out',
                  isActiveLink(item.href)
                    ? 'bg-[#C97B5A] text-white shadow-sm'
                    : 'text-Color-Scheme-1-Text dark:text-Color-Neutral-Lightest hover:bg-[#C97B5A]/10 hover:text-[#C97B5A] dark:hover:bg-[#E5A884]/10 dark:hover:text-[#E5A884]'
                )}
              >
                {item.label}
              </button>
            ))}
          </div>

        </nav>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={toggleMobileMenu}
            className="p-2.5 rounded-lg hover:bg-[#C97B5A]/10 dark:hover:bg-[#E5A884]/10 transition-all duration-200 ease-in-out"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-Color-Neutral-Darkest dark:text-Color-Neutral-Lightest hover:text-[#C97B5A] dark:hover:text-[#E5A884] transition-colors" />
            ) : (
              <Menu className="w-6 h-6 text-Color-Neutral-Darkest dark:text-Color-Neutral-Lightest hover:text-[#C97B5A] dark:hover:text-[#E5A884] transition-colors" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden overflow-hidden bg-[#F5E6D3] dark:bg-Color-Neutral-Darkest border-t border-[#E8D5C4] dark:border-Color-Neutral-Dark">
          <nav className="px-4 py-6 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.label}
                onClick={() => handleNavigation(item.href)}
                className={cn(
                  'block w-full text-left py-3.5 px-5 rounded-lg font-cairo text-base font-medium transition-all duration-200 ease-in-out',
                  isActiveLink(item.href)
                    ? 'bg-[#C97B5A] text-white shadow-sm'
                    : 'text-Color-Scheme-1-Text dark:text-Color-Neutral-Lightest hover:bg-[#C97B5A]/10 hover:text-[#C97B5A] dark:hover:bg-[#E5A884]/10 dark:hover:text-[#E5A884]'
                )}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};